import { css, cx } from '@linaria/atomic';
import { theme } from '@frontend/theme';

export type LiStyleProps = {
  disabled?: boolean;
  active?: boolean;
  destructive?: boolean;
  darkMode?: boolean;
  preserveCasing?: boolean;
};

// ── Divider ───────────────────────────────────────────────────────────────────

const menuDividerBase: string = css`
  margin-top: ${theme.spacing(0.5)};
  margin-bottom: ${theme.spacing(0.5)};
  border-top: 1px solid ${theme.colors.neutral20};
`;

// ── Menu item ─────────────────────────────────────────────────────────────────

const menuItemBase: string = css`
  align-items: center;
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  height: 40px;
  min-height: 40px;
  justify-content: flex-start;
  margin: 0;
  outline: none;
  padding: ${theme.spacing(0, 2)};
  gap: ${theme.spacing(1)};
  position: relative;
  text-decoration: none;
  transition: background-color 250ms ease-out;

  span {
    color: inherit;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1;
    display: inline;
    text-align: start;
  }

  &:hover {
    background-color: ${theme.colors.neutral5};
  }
  &:focus {
    background-color: ${theme.colors.neutral5};
    outline: none;
  }
`;

const menuItemUppercaseFirst: string = css`
  > :first-letter {
    text-transform: uppercase;
  }
`;

const menuItemDark: string = css`
  color: ${theme.colors.white};
  fill: ${theme.colors.white};
  &:hover {
    background-color: ${theme.colors.neutral80};
  }
  &:focus {
    background-color: ${theme.colors.neutral80};
  }
`;

const menuItemDestructive: string = css`
  color: ${theme.colors.text.critical};
`;

const menuItemNonDestructive: string = css`
  svg {
    /* fill needs to be here in case the svg is inside a :visited <a> tag */
    fill: inherit;
  }
`;

const menuItemDisabled: string = css`
  color: black;
  cursor: default;
  pointer-events: none;
  &:hover,
  &:focus {
    outline: none;
  }
  span {
    color: ${theme.colors.status.disabled};
  }
  svg {
    fill: ${theme.colors.status.disabled};
    color: ${theme.colors.status.disabled};
  }
`;

const menuItemDisabledDark: string = css`
  span {
    color: ${theme.colors.neutral60};
  }
  svg {
    fill: ${theme.colors.neutral60};
    color: ${theme.colors.neutral60};
  }
`;

const menuItemActive: string = css`
  outline: none;
  &::before {
    content: '';
    background: ${theme.colors.primary50};
    height: 100%;
    width: 3px;
    left: 0;
    position: absolute;
  }
`;

const menuItemActiveLightBg: string = css`
  background-color: ${theme.colors.neutral5};
`;

const menuItemActiveDarkBg: string = css`
  background-color: ${theme.colors.neutral80};
`;

const menuItemStyle = (props?: LiStyleProps): string =>
  cx(
    menuItemBase,
    !props?.preserveCasing && menuItemUppercaseFirst,
    props?.darkMode && menuItemDark,
    props?.destructive ? menuItemDestructive : menuItemNonDestructive,
    props?.disabled && menuItemDisabled,
    props?.disabled && props?.darkMode && menuItemDisabledDark,
    props?.active && menuItemActive,
    props?.active && (props?.darkMode ? menuItemActiveDarkBg : menuItemActiveLightBg)
  );

// ── Menu item dropdown ────────────────────────────────────────────────────────

const menuItemDropdownExtra: string = css`
  flex-shrink: 0;
  column-gap: ${theme.spacing(1)};
`;

const menuItemDropdownStyle = (props?: LiStyleProps): string => cx(menuItemStyle(props), menuItemDropdownExtra);

// ── Menu ──────────────────────────────────────────────────────────────────────

const menuBase: string = css`
  background: ${theme.colors.white};
  border-radius: ${theme.borderRadius.medium};
  box-shadow: ${theme.shadows.heavy};
  display: flex;
  flex-direction: column;
  list-style: none;
  margin: 0;
  min-width: 100px;
  overflow: auto;
  padding: ${theme.spacing(1, 0)};
  z-index: ${theme.zIndex.popover};

  &:focus {
    outline: none;
  }

  h2 {
    border-left: 2px solid transparent;
    font-size: ${theme.fontSize(18)};
    margin: 0;
    padding: ${theme.spacing(1, 2)};
    text-transform: capitalize;
  }
`;

const menuDark: string = css`
  background: ${theme.colors.neutral90};
`;

const menuStyle = (darkMode: boolean): string => cx(menuBase, darkMode && menuDark);

// ── Exports ───────────────────────────────────────────────────────────────────

export const PopoverMenuStyles = {
  menuItem: (props: LiStyleProps): string => menuItemStyle(props),
  menuItemDropdown: (props: LiStyleProps): string => menuItemDropdownStyle(props),
  menu: (darkMode: boolean): string => menuStyle(darkMode),
  divider: menuDividerBase,
};
